import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col } from "reactstrap";
import { useParams } from "react-router-dom";
import { BreadcrumbContext } from "../../../../Layouts/Header";
import { VOLUNTEER_PATHS } from "Routes/paths";
import styles from "../css/volunteerdashboard.module.scss";
import profileImage from "../../../../assets/images/volunteer.png";
import CommonTabHeader from "Components/Common/CommonTabHeader";
import skeletonStyles from "../../../../assets/scss/common/viewSkeleton.module.scss";
import { useVolunteerService } from "../api/useVolunteerQuery";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import { useVolunteerStudyHistoryDetailsService } from "../../AssignedStudy/api/useVolunteerStudyHistoryDetailsService";
import {
  IconCheckCircle,
  IconHourglass,
  IconFlask,
  IconCalendar,
  IconShield,
  IconTrendingUp,
  IconFolder,
  IconRuler,
  IconScale,
  IconActivity,
  IconHeart,
  IconFileText,
  IconUser,
  IconPhone,
  studyHistoryColumns,
} from "../helper/volunteer.constant";
import { VolunteerDashboardData } from "../helper/volunteer.types";
import { formatDate } from "common/service/CommonService";

interface VolunteerDashboardProps {
  isTab?: boolean;
}

const VolunteerDashboard: React.FC<VolunteerDashboardProps> = ({ isTab }) => {
  const breadcrumbContext = useContext(BreadcrumbContext);
  const setBreadcrumbItems = breadcrumbContext?.setBreadcrumbItems;
  const [minLoading, setMinLoading] = useState(true);
  const { id } = useParams();

  useEffect(() => {
    const timer = setTimeout(() => setMinLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);

  const volunteerId = Number(id) || Number(sessionStorage.getItem("activevolunteerid"));
  const { DashboardSelectQuery, SelectQuery } = useVolunteerService({
    id: volunteerId || undefined,
    getDashboard : true,
  });

  const rawData: VolunteerDashboardData | undefined = DashboardSelectQuery?.data?.data?.[0];
  const selectData: any = SelectQuery?.data?.data?.[0] || {};

  const volunteer = rawData?.volunteerdata || {};
  const study = rawData?.studydata || {};
  const enrollment = rawData?.enrollmentdata || {};
  const adverseEvent = rawData?.adverseeventdata || {};

  const volunteerData: any = {
    ...rawData,
    ...volunteer,
    ...study,
    ...enrollment,
    ...adverseEvent,
    eligibility: rawData?.eligibilitystatus,
    attachments: volunteer?.attachments || [],
  };

  const rawParticipants = selectData?.participantsdata || 
                          SelectQuery?.data?.participantsdata || 
                          rawData?.participantsdata || 
                          DashboardSelectQuery?.data?.participantsdata || 
                          [];

  const participants = rawParticipants;

  const isLoading = minLoading || 
    DashboardSelectQuery.isLoading || DashboardSelectQuery.isFetching ||
    SelectQuery.isLoading || SelectQuery.isFetching;

  const washoutRemainingDays = volunteerData?.washoutdays ?? "-";

  const uploadedDocsCount = volunteerData?.attachments?.length || 0;
  const docValue = `${uploadedDocsCount}/2`;
  const docDesc = volunteerData?.attachments?.map((att: any) => att.attachmenttypetext).filter(Boolean).join(" / ") || "No documents";

  const eligibilityStatusText = (rawData?.eligibilitystatus || "").toLowerCase();
  const isEligible = rawData?.isEligible ?? false;
  const isUnderScreening = rawData?.isUnderScreening ?? false;
  const isMedicallyUnfit = rawData?.isMedicallyUnfit ?? false;

  const isWashoutActive = eligibilityStatusText === "washout active" || eligibilityStatusText === "washout" || eligibilityStatusText === "washoutactive";
  const isTemporarilyIneligible = !!rawData?.alcoholtest?.[0]?.isTemporaryIneligible;

  let eligibilityColorClass = styles.green;
  let eligibilityDesc = "Cleared for next study";
  if (isMedicallyUnfit) {
    eligibilityColorClass = styles.red;
    eligibilityDesc = "Medically Unfit";
  } else if (isTemporarilyIneligible) {
    eligibilityColorClass = styles.red;
    eligibilityDesc = "Alcohol Test Failed";
  } else if (isWashoutActive) {
    eligibilityColorClass = styles.orange;
    eligibilityDesc = "Washout Active";
  } else if (isUnderScreening) {
    eligibilityColorClass = styles.blue;
    eligibilityDesc = "Screening in Progress";
  } else if (isEligible) {
    eligibilityColorClass = styles.green;
    eligibilityDesc = "Eligible for study";
  } else if (rawData?.eligibilitystatus) {
    eligibilityDesc = "";
  }

  let fitnessText = "Pending";
  if (isEligible) {
    fitnessText = "Fit";
  } else if (isMedicallyUnfit) {
    fitnessText = "Unfit";
  } else if (isUnderScreening) {
    fitnessText = "Pending";
  }

  const parseNumber = (val: any) => {
    const num = Number(val);
    return isNaN(num) ? 0 : num;
  };

  const totalWashout = parseNumber(volunteerData?.totalwashoutdays);
  const remainingWashout = parseNumber(volunteerData?.washoutdays || volunteerData?.washoutremainingdays || volunteerData?.remainingdays);
  const elapsedWashout = Math.max(0, totalWashout - remainingWashout);
  const progressPercentage = totalWashout > 0 ? (elapsedWashout / totalWashout) * 100 : 0;

  const isStudyActive = ["active", "ongoing", "completed", "enrolled"].includes(
    (volunteerData?.studystatus || volunteerData?.status || "").toLowerCase()
  );

  // const { VolunteerStudyDetailsQuery } = useVolunteerStudyHistoryDetailsService({
  //   otherid: volunteerId || undefined,
  //   pageSize: 100,
  // });

  const studyHistoryData: any[] = rawData?.studyhistorydata?.studies || [];
  const isStudyHistoryLoading = false;



  const renderSkeleton = () => (
    <>
      {/* Emergency washout skeleton elements */}
      <div className={styles.topSectionGrid} style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "0.875rem", marginBottom: "1.25rem" }}>
        {/* Left Profile Card Skeleton */}
        <div className={styles.profileCardWrapper} style={{ gridColumn: "span 1" }}>
          <div className={styles.premiumCard} style={{ padding: "1.25rem", textAlign: "center" }}>
            <div className={`${skeletonStyles.skeleton} mx-auto mb-3`} style={{ width: "80px", height: "80px", borderRadius: "50%" }} />
            <div className={skeletonStyles.skeleton} style={{ height: "14px", width: "120px", margin: "0 auto 8px" }} />
            <div className={skeletonStyles.skeleton} style={{ height: "10px", width: "80px", margin: "0 auto 12px" }} />
            <div className="border-top pt-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="d-flex justify-content-between mb-2">
                  <div className={skeletonStyles.skeleton} style={{ height: "10px", width: "50px" }} />
                  <div className={skeletonStyles.skeleton} style={{ height: "10px", width: "60px" }} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Info Panels Skeletons */}
        <div style={{ gridColumn: "span 4", display: "grid", gridTemplateRows: "auto 1fr", gap: "1.25rem" }}>
          {/* General Information Card Skeleton */}
          <div className={styles.premiumCard}>
            <div className={styles.cardHeader}>
              <div className={skeletonStyles.skeleton} style={{ height: "14px", width: "140px" }} />
            </div>
            <div className={styles.cardBody}>
              <div className="row g-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="col-md-4 mb-2">
                    <div className={skeletonStyles.skeleton} style={{ height: "10px", width: "60px", marginBottom: "4px" }} />
                    <div className={skeletonStyles.skeleton} style={{ height: "12px", width: "110px" }} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Study History Card Skeleton */}
          <div className={styles.premiumCard}>
            <div className={styles.cardHeader}>
              <div className={skeletonStyles.skeleton} style={{ height: "14px", width: "100px" }} />
            </div>
            <div className={styles.cardBody}>
              <table className="table align-middle table-nowrap mb-0" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <th key={i}>
                        <div className={skeletonStyles.skeleton} style={{ height: "12px", width: "60px" }} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 3 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 5 }).map((_, j) => (
                        <td key={j}>
                          <div className={skeletonStyles.skeleton} style={{ height: "14px", width: j === 0 ? "80px" : "60px" }} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // Synchronize breadcrumbs inside useEffect
  useEffect(() => {
    const defaultBreadcrumbs = [
      { text: "Volunteers", link: VOLUNTEER_PATHS.LIST },
      { text: isTab ? "Volunteer Info" : "Volunteer Dashboard" },
    ];
    setBreadcrumbItems?.(defaultBreadcrumbs);

    return () => {
      setBreadcrumbItems?.([]);
    };
  }, [setBreadcrumbItems, isTab]);

  const handleCall = (phoneNumber: string, contactName: string) => {
    alert(`Initiating simulated phone call to ${contactName} at ${phoneNumber}...`);
  };

  return (
    <div style={{ overflowX: "hidden", minHeight: "100vh" }}>
      {/* Wrapper to absorb CommonTabHeader negative margins */}
      {!isTab && (
        <div style={{ padding: "0 12px", backgroundColor: "#fff" }}>
          <CommonTabHeader title="Volunteer Dashboard" />
        </div>
      )}

      <div className={styles.dashboardWrapper} style={isTab ? { paddingTop: 0 } : undefined}>
        {/* Sub Header / Action Pills */}
        <div className={styles.dashboardHeader}>
          <div className={styles.headerTitleSection}>
            {isLoading ? (
              <div className={skeletonStyles.skeleton} style={{ width: "240px", height: "14px", borderRadius: "4px" }} />
            ) : (
              <span style={{ fontSize: "0.8125rem", color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Dashboard View - Detail Study Management
              </span>
            )}
          </div>
          <div className={styles.headerActions}>
          </div>
        </div>

        <Container fluid className="p-0">
          {isLoading ? renderSkeleton() : (
            <>
              {/* ================= TOP SECTION (5-Column Grid Layout) ================= */}
              <div className={styles.topSectionGrid}>
                {/* Profile Card (Spans 2 rows) */}
                <div className={styles.profileCardWrapper}>
                  <div className={`${styles.premiumCard} ${styles.profileCard}`}>
                    <div className={styles.avatarWrapper}>
                      <img
                        src={volunteerData?.profileimg ? `${sessionStorage.getItem("filebaseUrl") ?? ""}${volunteerData.profileimg}` : profileImage}
                        alt={volunteerData?.fullname || "Volunteer Profile"}
                        onError={(e) => {
                          // Fallback to high quality inline avatar svg if resource does not load
                          e.currentTarget.src = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=200";
                        }}
                      />
                    </div>
                    <h5 className={styles.profileName}>{volunteerData?.fullname || "-"}</h5>
                    <div className={styles.profileCode}>
                      {volunteerData?.volunteercode ? `Code - ${volunteerData.volunteercode}` : "-"}
                    </div>

                    <div className={styles.profileStats}>
                      <div className={styles.statRow}>
                        <span className={styles.label}>Age</span>
                        <span className={styles.value}>{volunteerData?.age ? `${volunteerData.age} yrs` : "-"}</span>
                      </div>
                      <div className={styles.statRow}>
                        <span className={styles.label}>Gender</span>
                        <span className={styles.value}>{volunteerData?.gendertext || "-"}</span>
                      </div>
                      <div className={styles.statRow}>
                        <span className={styles.label}>Blood Group</span>
                        <span className={styles.value}>{volunteerData?.bloodgrouptext || "-"}</span>
                      </div>
                      <div className={styles.statRow}>
                        <span className={styles.label}>BMI</span>
                        <span className={styles.value}>{volunteerData?.bmi ? Number(volunteerData.bmi).toFixed(2) : "-"}</span>
                      </div>
                    </div>

                    <div className={styles.profileBadges}>
                      <div className={`${styles.badgePill} ${styles.green}`}>
                        {volunteerData?.enrollmentstatus || "-"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card 1 */}
                <div className={styles.statItemCard}>
                  <div className={styles.statHeader}>
                    <span className={styles.statTitle}>Eligibility Status</span>
                    <div className={`${styles.iconBadge} ${eligibilityColorClass}`}>
                      <IconCheckCircle />
                    </div>
                  </div>
                  <div>
                    <div className={`${styles.statValue} ${eligibilityColorClass}`}>{volunteerData?.eligibility || "-"}</div>
                    <span className={styles.statDesc}>{eligibilityDesc}</span>
                  </div>
                </div>

                {/* Card 2 */}
                <div className={styles.statItemCard}>
                  <div className={styles.statHeader}>
                    <span className={styles.statTitle}>Washout Remaining</span>
                    <div className={`${styles.iconBadge} ${styles.orange}`}>
                      <IconHourglass />
                    </div>
                  </div>
                  <div>
                    <div className={`${styles.statValue} ${styles.orange}`}>{washoutRemainingDays === "-" ? "-" : `${washoutRemainingDays} Days`}</div>
                    <span className={styles.statDesc}>{volunteerData?.washoutdays ? "Washout Active" : "-"}</span>
                  </div>
                </div>

                {/* Card 3 */}
                <div className={styles.statItemCard}>
                  <div className={styles.statHeader}>
                    <span className={styles.statTitle}>Active Study</span>
                    <div className={`${styles.iconBadge} ${styles.blue}`}>
                      <IconFlask />
                    </div>
                  </div>
                  <div>
                    <div className={`${styles.statValue} ${styles.blue}`}>{volunteerData?.assignedstudycode || "-"}</div>
                    <span className={styles.statDesc}>{volunteerData?.studytitle || "-"}</span>
                  </div>
                </div>

                {/* Card 4 */}
                <div className={styles.statItemCard}>
                  <div className={styles.statHeader}>
                    <span className={styles.statTitle}>Next Visit</span>
                    <div className={`${styles.iconBadge} ${styles.blue}`}>
                      <IconCalendar />
                    </div>
                  </div>
                  <div>
                    <div className={`${styles.statValue} ${styles.blue}`}>-</div>
                    <span className={styles.statDesc}>-</span>
                  </div>
                </div>

                {/* Card 5 */}
                <div className={styles.statItemCard}>
                  <div className={styles.statHeader}>
                    <span className={styles.statTitle}>Adverse Event</span>
                    <div className={`${styles.iconBadge} ${styles.red}`}>
                      <IconShield />
                    </div>
                  </div>
                  <div>
                    <div className={`${styles.statValue} ${styles.red}`}>
                      {volunteerData?.isAdverse ? 1 : "-"}
                    </div>
                    <span className={styles.statDesc}>
                      {volunteerData?.isAdverse ? (volunteerData?.eventterm || "Active AE") : "-"}
                    </span>
                  </div>
                </div>

                {/* Card 6 */}
                <div className={styles.statItemCard}>
                  <div className={styles.statHeader}>
                    <span className={styles.statTitle}>Study Progress</span>
                    <div className={`${styles.iconBadge} ${styles.blue}`}>
                      <IconTrendingUp />
                    </div>
                  </div>
                  <div>
                    <div className={`${styles.statValue} ${styles.blue}`}>-</div>
                    <span className={styles.statDesc}>-</span>
                  </div>
                </div>

                {/* Card 7 */}
                <div className={styles.statItemCard}>
                  <div className={styles.statHeader}>
                    <span className={styles.statTitle}>Documents</span>
                    <div className={`${styles.iconBadge} ${styles.yellow}`}>
                      <IconFolder />
                    </div>
                  </div>
                  <div>
                    <div className={styles.statValue}>{docValue}</div>
                    <span className={styles.statDesc}>{docDesc}</span>
                  </div>
                </div>

                {/* Card 8 */}
                <div className={styles.statItemCard}>
                  <div className={styles.statHeader}>
                    <span className={styles.statTitle}>Informed Consent</span>
                    <div className={`${styles.iconBadge} ${rawData?.icfdata?.signeddate ? styles.green : styles.orange}`}>
                      <IconCheckCircle />
                    </div>
                  </div>
                  <div>
                    <div className={`${styles.statValue} ${rawData?.icfdata?.signeddate ? styles.green : styles.orange}`}>
                      {rawData?.icfdata?.signeddate ? "Signed" : "-"}
                    </div>
                    <span className={styles.statDesc}>
                      {rawData?.icfdata?.icfversion ? `Version v${rawData.icfdata.icfversion}` : "-"}
                    </span>
                  </div>
                </div>
              </div>

              {/* ================= MIDDLE SECTION ================= */}
              <Row className="g-3 mb-3">
                {/* Current Study Info (6 columns) */}
                <Col lg={6} md={12}>
                  <div className={styles.premiumCard}>
                    <div className={styles.cardHeader}>
                      <span>Current Study Information</span>
                    </div>
                    <div className={styles.cardBody}>
                      <div className={styles.studyInfoGrid}>
                        {/* Left Column */}
                        <div className={styles.studyInfoColumn}>
                          <div className={styles.studyInfoRow}>
                            <span className={styles.label}>Study Code</span>
                            <span className={styles.value}>{volunteerData?.assignedstudycode || "-"}</span>
                          </div>
                          <div className={styles.studyInfoRow}>
                            <span className={styles.label}>Study Title</span>
                            <span className={styles.value}>{volunteerData?.studytitle || "-"}</span>
                          </div>
                          <div className={styles.studyInfoRow}>
                            <span className={styles.label}>Study Type</span>
                            <span className={styles.value}>{volunteerData?.studytypetext || "-"}</span>
                          </div>
                          <div className={styles.studyInfoRow}>
                            <span className={styles.label}>Study Design</span>
                            <span className={styles.value}>{volunteerData?.studydesigntext || "-"}</span>
                          </div>
                          <div className={styles.studyInfoRow}>
                            <span className={styles.label}>Sponsor</span>
                            <span className={styles.value}>{volunteerData?.sponsorname || "-"}</span>
                          </div>
                        </div>

                        {/* Right Column */}
                        <div className={styles.studyInfoColumn}>
                          <div className={styles.studyInfoRow}>
                            <span className={styles.label}>CRO Name</span>
                            <span
                              className={styles.value}
                              title={volunteerData?.croname || undefined}
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "block",
                                maxWidth: "180px",
                                marginLeft: "auto"
                              }}
                            >
                              {volunteerData?.croname || "-"}
                            </span>
                          </div>
                          <div className={styles.studyInfoRow}>
                            <span className={styles.label}>Periods</span>
                            <span className={styles.value}>{volunteerData?.numberofperiods || "-"}</span>
                          </div>
                          <div className={styles.studyInfoRow}>
                            <span className={styles.label}>Total Subjects</span>
                            <span className={styles.value}>{volunteerData?.totalsubjects || "-"}</span>
                          </div>
                          <div className={styles.studyInfoRow}>
                            <span className={styles.label}>Extra Subjects</span>
                            <span className={styles.value}>{volunteerData?.extrasubjects || "-"}</span>
                          </div>
                          <div className={styles.studyInfoRow}>
                            <span className={styles.label}>Study Status</span>
                            <span className={`${styles.value} ${isStudyActive ? styles.activeBadge : ""}`}>
                              {volunteerData?.studystatus || "-"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>

                {/* Eligibility Status Checklist (3 columns) */}
                <Col lg={3} md={6}>
                  <div className={styles.premiumCard}>
                    <div className={styles.cardHeader}>
                      <span>Eligibility Status</span>
                    </div>
                    <div className={styles.cardBody}>
                      <div className={styles.eligibilityList}>
                        <div className={styles.eligibilityRow}>
                          <span>Eligible for Study</span>
                          <span className={`${styles.dot} ${isEligible ? styles.green : ""}`}></span>
                        </div>
                        <div className={styles.eligibilityRow}>
                          <span>Under Screening</span>
                          <span className={styles.dot} style={isUnderScreening ? { backgroundColor: "#3b82f6", borderColor: "#3b82f6" } : undefined}></span>
                        </div>
                        <div className={styles.eligibilityRow}>
                          <span>Washout Active</span>
                          <span className={`${styles.dot} ${isWashoutActive ? styles.orange : ""}`}></span>
                        </div>
                        <div className={styles.eligibilityRow}>
                          <span>Medically Unfit</span>
                          <span className={styles.dot} style={isMedicallyUnfit ? { backgroundColor: "#ef4444", borderColor: "#ef4444" } : undefined}></span>
                        </div>
                        <div className={styles.eligibilityRow}>
                          <span>Temporarily Ineligible</span>
                          <span className={styles.dot} style={isTemporarilyIneligible ? { backgroundColor: "#ef4444", borderColor: "#ef4444" } : undefined}></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>

                {/* Washout Status (3 columns) */}
                <Col lg={3} md={6}>
                  <div className={styles.premiumCard}>
                    <div className={styles.cardHeader}>
                      <span>Washout Status</span>
                    </div>
                    <div className={styles.cardBody}>
                      <div className={styles.keyValueList}>
                        <div className={styles.keyValueRow}>
                          <span className={styles.label}>Last Participation</span>
                          <span className={styles.value}>{volunteerData?.lastparticipation || volunteerData?.lastparticipationdate || "-"}</span>
                        </div>
                        <div className={styles.keyValueRow}>
                          <span className={styles.label}>Washout Start</span>
                          <span className={styles.value}>{volunteerData?.washoutstart || volunteerData?.washoutstartdate || volunteerData?.lastparticipation || "-"}</span>
                        </div>
                        <div className={styles.keyValueRow}>
                          <span className={styles.label}>Washout End</span>
                          <span className={styles.value}>{volunteerData?.washoutend || volunteerData?.washoutenddate || "-"}</span>
                        </div>
                        <div className={styles.keyValueRow}>
                          <span className={styles.label}>Remaining Days</span>
                          <span className={`${styles.value} text-warning`} style={{ color: "#d97706" }}>
                            {remainingWashout > 0 ? `${remainingWashout} days` : "-"}
                          </span>
                        </div>
                        <div className={styles.keyValueRow}>
                          <span className={styles.label}>Next Eligible Date</span>
                          <span className={styles.value}>{volunteerData?.nexteligibledate || volunteerData?.washoutend || volunteerData?.washoutenddate || "-"}</span>
                        </div>
                      </div>

                      <div className={styles.washoutProgressContainer}>
                        <div className={styles.progressBarLabel}>
                          <span>Elapsed</span>
                          <span>{totalWashout > 0 ? `${elapsedWashout} / ${totalWashout} days` : "-"}</span>
                        </div>
                        <div className={styles.progressBarOuter}>
                          <div className={styles.progressBarInner} style={{ width: `${progressPercentage}%` }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* ================= MIDDLE-BOTTOM SECTION ================= */}
              <Row className="g-3 mb-3">
                {/* Medical Summary (4 columns) */}
                <Col lg={4} md={6}>
                  <div className={styles.premiumCard}>
                    <div className={styles.cardHeader}>
                      <span>Medical Summary</span>
                    </div>
                    <div className={styles.cardBody}>
                      <div className={styles.medicalGrid}>
                        <div className={styles.medicalItemCard}>
                          <span className={styles.icon}><IconRuler /></span>
                          <span className={styles.value}>{volunteerData?.height ? `${volunteerData.height} cm` : "-"}</span>
                          <span className={styles.label}>Height</span>
                        </div>
                        <div className={styles.medicalItemCard}>
                          <span className={styles.icon}><IconScale /></span>
                          <span className={styles.value}>{volunteerData?.weight ? `${volunteerData.weight} kg` : "-"}</span>
                          <span className={styles.label}>Weight</span>
                        </div>
                        <div className={styles.medicalItemCard}>
                          <span className={styles.icon}><IconActivity /></span>
                          <span className={styles.value}>{volunteerData?.bmi ? Number(volunteerData.bmi).toFixed(2) : "-"}</span>
                          <span className={styles.label}>BMI</span>
                        </div>
                        <div className={styles.medicalItemCard}>
                          <span className={styles.icon}><IconHeart /></span>
                          <span className={styles.value}>{fitnessText}</span>
                          <span className={styles.label}>Fitness</span>
                        </div>
                      </div>

                      <div className={styles.keyValueList}>
                        <div className={styles.keyValueRow}>
                          <span className={styles.label}>Blood Pressure</span>
                          <span className={styles.value}>{volunteerData?.bloodpressure || "-"}</span>
                        </div>
                        <div className={styles.keyValueRow}>
                          <span className={styles.label}>Pulse Rate</span>
                          <span className={styles.value}>{volunteerData?.pulserate ? `${volunteerData.pulserate} bpm` : "-"}</span>
                        </div>
                        <div className={styles.keyValueRow}>
                          <span className={styles.label}>Temperature</span>
                          <span className={styles.value}>{volunteerData?.temperature ? `${volunteerData.temperature}°F` : "-"}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>

                {/* Laboratory Status (4 columns) */}
                <Col lg={4} md={6}>
                  <div className={styles.premiumCard}>
                    <div className={styles.cardHeader}>
                      <span>Laboratory Status</span>
                    </div>
                    <div className={styles.cardBody}>
                      <div className={styles.docChecklist}>
                        {rawData?.labtestdata && rawData.labtestdata.length > 0 ? (
                          rawData.labtestdata.map((lab: any, idx: number) => (
                            <div className={styles.docRow} key={lab.studylabtestid || idx}>
                              <div className={styles.docLabel}>
                                <IconFlask />
                                <span title={lab.reportname || "Lab Report"}>
                                  {lab.reportname || "Lab Report"}
                                </span>
                              </div>
                              <span className={`${styles.docBadge} ${lab.isverified ? styles.verified : styles.pending}`}>
                                {lab.isverified ? "Verified" : "Pending Verification"}
                              </span>
                            </div>
                          ))
                        ) : (
                          <div className="text-muted small text-center py-4">No Lab Reports Available</div>
                        )}
                      </div>
                    </div>
                  </div>
                </Col>

                {/* Informed Consent (4 columns) */}
                <Col lg={4} md={12}>
                  <div className={styles.premiumCard}>
                    <div className={styles.cardHeader}>
                      <span>Informed Consent</span>
                    </div>
                    <div className={styles.cardBody}>
                      <div className={styles.keyValueList}>
                        <div className={styles.keyValueRow}>
                          <span className={styles.label}>Consent Version</span>
                          <span className={styles.value}>{rawData?.icfdata?.icfversion ? `v${rawData.icfdata.icfversion}` : "-"}</span>
                        </div>
                        <div className={styles.keyValueRow}>
                          <span className={styles.label}>Signed Date</span>
                          <span className={styles.value}>{rawData?.icfdata?.signeddate ? formatDate(rawData.icfdata.signeddate) : "-"}</span>
                        </div>
                        <div className={styles.keyValueRow}>
                          <span className={styles.label}>Language</span>
                          <span className={styles.value}>{rawData?.icfdata?.language || "-"}</span>
                        </div>
                      </div>

                      <div className="mt-3">
                        <span className={styles.label} style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b" }}>Physician Remarks</span>
                        <div className={styles.remarksBubble}>
                          <p className={styles.quoteText}>
                            {rawData?.icfdata?.remarks || "-"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* ================= LOWER SECTION ================= */}
              <Row className="g-3 mb-3">
                {/* Adverse Events & Safety (4 columns) */}
                <Col lg={4} md={6}>
                  <div className={`${styles.premiumCard} ${styles.safetyContainer}`}>
                    <div className={styles.cardHeader}>
                      <span>Adverse Events & Safety</span>
                    </div>
                    <div className={styles.cardBody} style={{ minHeight: "150px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                      {volunteerData?.isAdverse ? (
                        <div className={styles.keyValueList}>
                          <div className={styles.keyValueRow}>
                            <span className={styles.label}>Event Term</span>
                            <span className={styles.value}>{volunteerData?.eventterm || "-"}</span>
                          </div>
                          <div className={styles.keyValueRow}>
                            <span className={styles.label}>Severity</span>
                            <span className={styles.value}>{volunteerData?.severitygradetext || "-"}</span>
                          </div>
                          <div className={styles.keyValueRow}>
                            {/* <span className={styles.label}>Status</span> */}
                            {/* <span 
                              className={styles.value}
                              style={{
                                color: volunteerData?.eventstatustext?.toLowerCase() === "ongoing" ? "#dc2626" : "#065f46",
                                fontWeight: 700
                              }}
                            >
                              {volunteerData?.eventstatustext || "-"}
                            </span> */}
                          </div>
                        </div>
                      ) : (
                        <div className="text-muted small text-center py-4">
                          No Adverse Events Reported
                        </div>
                      )}
                    </div>
                  </div>
                </Col>

                {/* Volunteer Documents (4 columns) */}
                <Col lg={4} md={6}>
                  <div className={styles.premiumCard}>
                    <div className={styles.cardHeader}>
                      <span>Volunteer Documents</span>
                    </div>
                    <div className={styles.cardBody}>
                      <div className={styles.docChecklist}>
                        {volunteerData?.attachments && volunteerData.attachments.length > 0 ? (
                          volunteerData.attachments.map((doc: any, idx: number) => (
                            <div className={styles.docRow} key={doc.volunteerattachmentid || doc.attachmentid || idx}>
                              <div className={styles.docLabel}>
                                <IconFileText />
                                <span title={doc.name || doc.attachmenttypetext || doc.attachmentTypetext || "Document"}>
                                  {doc.name || doc.attachmenttypetext || doc.attachmentTypetext || "Document"}
                                </span>
                              </div>
                              {/* <span className={`${styles.docBadge} ${doc.isverified ? styles.verified : styles.pending}`}>
                                {doc.isverified ? "Verified" : "Pending Verification"}
                              </span> */}
                            </div>
                          ))
                        ) : (
                          <div className="text-muted small text-center py-4">No Documents Uploaded</div>
                        )}
                      </div>
                    </div>
                  </div>
                </Col>

                {/* Emergency & Contact Information (4 columns) */}
                <Col lg={4} md={12}>
                  <div className={styles.premiumCard}>
                    <div className={styles.cardHeader}>
                      <span>Emergency & Contact Information</span>
                    </div>
                    <div className={styles.cardBody}>
                      <div className={styles.contactContainer}>
                        {/* 1. Dynamic Participants List */}
                        {participants && participants.map((participant: any, index: number) => {
                          const isSuperAdmin = participant.role?.trim()?.toLowerCase() === "super admin";
                          return (
                            <div className={styles.contactItem} key={`participant-${index}`}>
                              <div className={styles.contactDetails}>
                                <span className={styles.contactIconBox}><IconUser /></span>
                                <div className={styles.contactInfoText}>
                                  {!isSuperAdmin && (
                                    <span className={styles.title}>{participant.role || "Study Participant"}</span>
                                  )}
                                  <span className={styles.name}>{participant.userdisplayname || participant.username || "-"}</span>
                                  <span className={styles.phone}>{participant.contactnumber || "-"}</span>
                                </div>
                              </div>
                              <button
                                className={styles.callBtn}
                                title={isSuperAdmin ? "Call Participant" : `Call ${participant.role || "Participant"}`}
                                onClick={() => handleCall(participant.contactnumber || "", participant.userdisplayname || participant.username || "")}
                                disabled={!participant.contactnumber}
                                style={!participant.contactnumber ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                              >
                                <IconPhone />
                              </button>
                            </div>
                          );
                        })}

                        {/* 2. Empty State */}
                        {participants.length === 0 && (
                          <div className="text-muted small text-center py-4">No Contact Information Available</div>
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* ================= BOTTOM MOST ROW ================= */}
              <Row className="g-3">
                {/* Study History Table (12 columns) */}
                <Col lg={12}>
                  <div className={styles.premiumCard}>
                    <div className={styles.cardHeader}>
                      <span>Study History</span>
                    </div>
                    <div className={styles.cardBody}>
                      <CommonTableIndexWise
                        columns={studyHistoryColumns}
                        data={studyHistoryData}
                        hideAction={true}
                        paginationShow={false}
                        loading={isLoading || isStudyHistoryLoading}
                        totalRecords={studyHistoryData.length}
                        moduleName="Volunteer"
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </Container>
      </div>
    </div>
  );
};

export default VolunteerDashboard;
